import { AfterViewInit, Component, ElementRef, Inject, OnDestroy, OnInit, Renderer2, Signal, signal, ViewChild } from '@angular/core';
import { Gesture, GestureController, GestureDetail } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { DismissAnimation, IPredictWonToastData, IToastButtonData, SopoToastSettings, ToastIcon } from '../../interfaces/toast';
import { SharedPipesModule } from 'src/app/shared/pipes/shared.pipes.module';

import { toSignal } from '@angular/core/rxjs-interop';
import { ResizeService } from 'src/app/shared/services/resize/resize.service';

@Component({
    selector: 'app-toast',
    templateUrl: './sopo-toast.component.html',
    styleUrls: ['./sopo-toast.component.scss'],
    imports: [
        SharedPipesModule,
    ],
    standalone: true,
    host: {
        '[style.--start-x]': 'translateX()',
        '[style.--opacity]': 'opacity()',
        '[style.fontSize]': 'fontSize()',
    }
})
export class SopoToastComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('toast', { static: true }) toast: ElementRef;
    private _gesture: Gesture;
    private _closeAnimationTime = 300; // Close animation time in milliseconds.
    private _closeTriggerDistance = 50; // The distance to trigger the close animation in px.
    private _destroy = new BehaviorSubject(false);
    private _toastClosed = new BehaviorSubject(false);
    private _dismissAfterTimeout: NodeJS.Timeout;
    private _dismissing = false;

    protected translateX = signal('0px');
    protected opacity = signal(1);
    protected fontSize = toSignal(this.resizeService.getSopoToastFontSize(), { initialValue: '16px' });
    protected icon: ToastIcon = 'prediction';
    protected header = '';
    protected message = '';
    protected cssClasses: string | string[] = '';
    protected predictionWon: IPredictWonToastData | undefined;
    protected button: IToastButtonData | undefined;

    constructor(
        @Inject('ToastSettings') private _settings: SopoToastSettings,
        private _el: ElementRef,
        private _gestureCtrl: GestureController,
        private _renderer: Renderer2,
        private resizeService: ResizeService
    ) {
        if (_settings.header) this.header = _settings.header;
        if (_settings.message) this.message = _settings.message;
        if (_settings.cssClasses) this.cssClasses = _settings.cssClasses;
        switch (_settings.animationSpeed) {
            case 'fast':
                this._closeAnimationTime = 200;
                break;
            case 'slow':
                this._closeAnimationTime = 600;
                break;
            default:
                this._closeAnimationTime = 400;
                break;
        }
        if (this._settings.dismissAfter > 0) {
            setTimeout(() => {
                this.dismiss(_settings.autoDismissAnimation);
            }, this._settings.dismissAfter);
        }
        if (_settings?.data?.predictWon?.amountWon && _settings?.data?.predictWon?.currency) this.predictionWon = _settings.data.predictWon;
        if (_settings?.data?.button) this.button = _settings.data.button;
        if (_settings?.toastIcon) this.icon = _settings.toastIcon;
    }

    ngOnInit() { }

    ngAfterViewInit() {
        this._renderer.addClass(this.toast.nativeElement, `${this._settings.animationSpeed}`);
        this._renderer.addClass(this.toast.nativeElement, `${this._settings.enterAnimation}`);

        this._gesture = this._gestureCtrl.create({
            el: this._el.nativeElement,
            onStart: (detail) => this.onStart(detail),
            onMove: (detail) => this.onMove(detail),
            onEnd: (detail) => this.onEnd(detail),
            direction: 'x',
            gestureName: 'swipeToClose',
            passive: false,
        }, false);

        this._gesture.enable();
    }

    private onStart(detail: GestureDetail) {
        detail.event?.stopPropagation();
        detail.event?.preventDefault();
        this._renderer.removeStyle(this.toast.nativeElement, 'transition');
    }

    private onMove(detail: GestureDetail) {
        detail.event?.stopPropagation();
        detail.event?.preventDefault();
        const { deltaX } = detail;
        this.translateX.set(`${deltaX}px`);
        const opacity = Math.min(Math.max(300 - Math.abs(deltaX) + 50, 0) / 300, 1);
        this.opacity.set(opacity);
    }

    private onEnd(detail: GestureDetail) {
        const { deltaX } = detail;
        detail.event?.stopPropagation();
        detail.event?.preventDefault();
        if (Math.abs(deltaX) < this._closeTriggerDistance) {
            this.opacity.set(1);
            this.translateX.set('0px');
        } else {
            const direction: DismissAnimation = deltaX < 0 ? 'slide-out-to-left' : 'slide-out-to-right';
            this.dismiss(direction);
        }
    }

    getToastClosed () {
        return this._toastClosed.asObservable().pipe(
            filter(value => value),
        );
    }

    getDestroyObservable() {
        return this._destroy.asObservable().pipe(
            filter(destroy => destroy),
            distinctUntilChanged(),
        );
    }

    async dismiss(dismissAnimation: DismissAnimation = 'slide-out-to-right') {
        if (this._dismissing) return;
        this._dismissing = true;
        if (this._dismissAfterTimeout) {
            clearTimeout(this._dismissAfterTimeout);
        }
        await this.playDismissAnimation(dismissAnimation);
    }

    protected onToastButtonClick() {
        this.dismiss();
        this._settings?.data?.button?.handler();
    }

    private async playDismissAnimation(dismissAnimation: DismissAnimation): Promise<void> {
        this._gesture.enable(false);
        this._renderer.setStyle(this._el.nativeElement, 'pointer-events', 'none');
        const dismissClass = dismissAnimation;
        this._renderer.addClass(this.toast.nativeElement, dismissClass);

        await new Promise((res, _) => {
            setTimeout(res, this._closeAnimationTime + 50);
        });

        this._destroy.next(true);
    }

    ngOnDestroy() {
        this._gesture?.destroy();
        this._toastClosed.next(true);
    }
}
